import { useEffect } from "react";
import { CallSession } from "../../models/CallSession";
import { getCurrentUnroutedContactFlowStage } from "../calls/LeadListView/UnroutedContactFlowStageList/service";
import { TelephonyState, TelephonyContextType, TelephonyCohearingState } from "./TelephonyContext";
import { TelephonyServiceCallInfo } from "./TelephonyService";
import { globalPhoneNumberStore } from "../calls/PhoneNumberDispatcher";
import { CallContactInformation, CallSelector, chooseCallerPhoneNumber as chooseCallerPhoneNumberService, createTelephonyHeartbeat, getCallContactInformation, isBusyInAnotherTab, setBusyInAnotherTab, ChooseCallerPhoneNumberParams } from "./service";
import { minimizeStack } from "../../utils/error";
import { retry } from "../../utils/async";
import { UserCallStatus } from "../../models/CallStatisticsItem";
import LeadList from "../../models/LeadList";
import { LeadListMode } from "../../models/LeadListMode";
import { globalUserStore } from "../user/UserDispatcher";
import { endPredictiveMode } from "./LeadListModeList/service";
import { getUserId, isRemovableSupportLogin } from "../../utils/user";
import { Feature } from "../../models/Feature";
import { listCalls } from "../calls/service";
import { CallFilters, CallConnectionState } from "../../models/CallFilters";
import { text } from "../../utils/i18n";
import { getContact } from "../contacts/service";
import { Country } from "../../models/Country";
import { AsynchronousNotification } from "../../models/AsynchronousNotification";
import { createCallComment } from "../calls/CallList/service";
import Comment from "../../models/Comment";
import { createContactComment } from "../contacts/ContactList/service";
import { ContactEventContextType } from "../contacts/ContactEventContextProvider";
import ContactField from "../../models/ContactField";
import { listContactPhoneNumbers } from "s2-contact-phone-utils";
import { getFieldMapper } from "../../utils/fields";
import { History } from "history";

function isTelephonyEnabled() {
  return globalUserStore.state.organization?.hasFeature(Feature.Dial);
}

window.addEventListener('unload', () => {
  //  Updates the call activity to `inactive` right before the app is closed.
  if (!isTelephonyEnabled() || isRemovableSupportLogin()) {
    return;
  }

  createTelephonyHeartbeat({
    userStatus: UserCallStatus.Inactive,
    currentCallIds: null,
    currentPredictiveLeadListId: null,
    currentCohearingUserId: null
  });

  if (!isBusyInAnotherTab()) {
    endPredictiveMode();
  }
});

window.addEventListener('unload', () => setBusyInAnotherTab(false))

const notificationAudio = new Audio(require('./CallNotification.wav').default);
const inboundNotificationAudio = new Audio(require('./CallInboundNotification.wav').default);

export function getCallNotificationAudio() {
  return notificationAudio;
}

export function getCallInboundNotificationAudio() {
  return inboundNotificationAudio;
}

/**
 * In sequential mode, gets the next UCF stage and assigns it to the call session.
 */
export async function refreshUnrouchedContactFlowStage(session: CallSession) {
  const { contactPhoneNumber, leadListId, unroutedContactFlowId } = session;
  const controller = new AbortController();

  try {
    //  The user should not have to wait some significant time for this.
    const signalTimeoutId = setTimeout(() => controller.abort(), 2500);
    const stage = await getCurrentUnroutedContactFlowStage(unroutedContactFlowId, contactPhoneNumber, leadListId, { signal: controller.signal });
    clearTimeout(signalTimeoutId);

    return stage;
  } catch (error) {
    console.error("Error fetching next unrouted contact flow %s stage", error);
    return null;
  }
}

type RefreshCallContactInformationParams = {
  selector: CallSelector;
  sessionId: string;
} & Pick<TelephonyContextType, 'updateCallSession'>

/**
 * Gets contact fields and assigns it to a call session.
 */
export async function refreshCallContactInformation(args: RefreshCallContactInformationParams) {
  //  Need to retry a few times with minimal delay.
  // The call might get updated with the selector milliseconds after this function is invoked.
  const result: CallContactInformation | null = await retry(
    () => getCallContactInformation(args.selector)
      .catch(error => {
        if (error.code === "202401121037") {
          //  "Call is disconnected" happens occasionally when the call is immediately
          //  hung up after answer. Do not retry the fetch.
          return null;
        }

        //  Note that the call might not exist, even though a signal was received from Telnect, so retry.

        throw error;
      }),
    { sleep: 1000 }
  ).catch(error => {
    console.error("alert: Could not fetch call contact information", {
      ...error,
      sessionId: args.sessionId,
      name: error.name,
      stack: minimizeStack(error),
      message: error.message,
      selector: args.selector
    });

    return null;
  });

  if (!result) {
    //  Canceled.
    return;
  }

  let sessionUpdate: NotReadOnly<Partial<CallSession>> = {};
  if (result.contactId) {
    sessionUpdate.contactId = result.contactId;
  }

  if (result.contactInfo) {
    sessionUpdate.contactInfo = result.contactInfo;
  }

  args.updateCallSession(sessionUpdate, args.sessionId);
}

/**
 * Refreshes phone numbers and chooses a random one.
 */
export async function chooseCallerPhoneNumber(args: ChooseCallerPhoneNumberParams) {
  const phoneNumbers = await globalPhoneNumberStore.state.refresh();
  return chooseCallerPhoneNumberService(phoneNumbers, args);
}

export async function checkMicrophone(): Promise<{ ok: boolean; muted?: boolean; }> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: false
    });

    if (!stream) {
      return { ok: false };
    }

    const tracks = stream.getAudioTracks();
    const muted = !tracks.find(track => !track.muted);

    try {
      //  Hides the "This tab is using your microphone" in Chrome.
      //  https://stackoverflow.com/a/46611605
      tracks.forEach(track => track.stop());
    } catch (error) {
      console.error("Error stopping audio tracks", error);
    }

    return { ok: true, muted };
  } catch (error) {
    console.error("getUserMedia error:", error);
    return { ok: false };
  }
}

export function getMicrophoneMutedNotification() {
  return new AsynchronousNotification({
    id: "inputDeviceMuted",
    title: text`Mikrofonen är avstängd`,
    body: text`Aktivera mikrofonen i din enhets systeminställningar.`,
    icon: {
      name: "microphone-slash"
    },
    actions: {
      default: {
        onClick() {
          return true;
        },
        chevron: false
      }
    }
  })
}

/**
 * Plays the notification sound for dial.
 */
export function playCallNotification() {
  if (notificationAudio.readyState === 4) {
    notificationAudio.play();
  }
}

export function playCallInboundNotification() {
  if (inboundNotificationAudio.readyState === 4) {
    inboundNotificationAudio.play();
  }
}

type CreateCallSessionFromCallInfoParams = {
  info: TelephonyServiceCallInfo;
  timestamps: Pick<CallSession, 'connectedAt' | 'connectingAt'>;
  leadList?: LeadList;
  state: TelephonyState;
}

export function getCallSessionFromCallInfo({ info, timestamps, leadList, state }: CreateCallSessionFromCallInfoParams) {
  return new CallSession({
    easyTelecomUserCallIds: [info.easyTelecomUserCallId],
    userPhoneNumber: info.callerPhoneNumber,
    contactPhoneNumber: info.contactPhoneNumber,
    contactId: info.contactId,
    contactInfo: info.contactFields,
    leadListId: leadList?.id, // Ska denna vara currentSession.leadListId?
    unroutedContactFlowId: leadList?.unroutedContactFlow?.enabled ? leadList.unroutedContactFlow.flowId : null,
    leadListMode: state.leadListMode,
    ...timestamps
  });
}

/**
 * Returns update to a call session with information from the telephony service.
 */
export function getCallSessionUpdateFromTelephonyService(info: TelephonyServiceCallInfo, session: CallSession): Partial<CallSession> {
  let update: Partial<CallSession> = {
    easyTelecomUserCallIds: [...session.easyTelecomUserCallIds, info.easyTelecomUserCallId],
    userPhoneNumber: info.callerPhoneNumber || session.userPhoneNumber,
  };

  //  Check if contact phone number is set before modifying it.
  //  A bug was seemingly introduced in easytelecom as of 2023-09-17 which makes it the caller phone number,
  //  seems to happen after answer.
  if (info.contactPhoneNumber && !session.contactPhoneNumber) {
    update = {
      ...update,
      contactPhoneNumber: info.contactPhoneNumber
    };
  }

  if (info.contactFields) {
    //  Does not require CallsGetContactData right, see https://trello.com/c/6wa84tIn
    update = {
      ...update,
      contactInfo: info.contactFields
    };
  }

  if (info.contactId) {
    update = {
      ...update,
      contactId: info.contactId
    };
  }

  return update;
}

export async function notifyAnsweredCallEnded(callId: string, history: History<any>, notify: (notification: AsynchronousNotification) => void) {
  const [call] = await listCalls(new CallFilters({
    ids: [callId],
    connectionState: CallConnectionState.Any,
  }));

  if (call) {
    notify(new AsynchronousNotification({
      title: text`Samtalet är avslutat`,
      body: text`Klicka för att visa samtalet i samtalshistoriken.`,
      icon: { name: "phone-slash" },
      actions: {
        default: {
          onClick() {
            history.push(`/calls?callId=${call.id}`);
            return true;
          }
        },
        confirm: {
          onClick() {
            return true;
          },
          weight: 'dimmed',
          children: text`Stäng`
        },
      }
    }));

    return;
  }

  notify(new AsynchronousNotification({
    title: text`Kunde inte svara samtalet`,
    body: text`Samtalet är avslutat.`,
    icon: { name: "phone-slash" },
    actions: {
      confirm: {
        onClick() {
          return true;
        },
        weight: 'dimmed',
        children: text`Stäng`
      },
    }
  }));
}

type UserCallStatusParams = {
  callSession?: CallSession | null | undefined;
  leadListMode?: LeadListMode;
  leadList?: LeadList;
  cohearing?: TelephonyCohearingState;
};

export function getUserCallStatus({
  callSession, leadList, leadListMode, cohearing
}: UserCallStatusParams): UserCallStatus {
  if (callSession?.isCohear) {
    return UserCallStatus.Cohearing;
  }

  if (callSession?.isActive && callSession.connectedAt) {
    return UserCallStatus.Connected;

  } else if (callSession?.isActive) {
    return UserCallStatus.Connecting;
  }

  if (cohearing?.user?.isActive) {
    return UserCallStatus.CohearingAwaitingCall;
  }

  //  Om man ringer prediktivt så är det mer intressant att veta om man inväntar samtal än om man väljer utfall.
  if ((leadList || callSession?.leadListId) &&
    leadListMode === LeadListMode.Predictive) {

    if (leadList?.predictiveWaitForEvaluation ? !callSession : true) {
      return UserCallStatus.PredictiveAwaitingCall;
    }
  }

  if (callSession?.isEvaluationPending) {
    return UserCallStatus.EvaluatingCall;
  }

  return UserCallStatus.Inactive;
}

type CallSessionAutoRefreshContactParams = {
  fields: ContactField[];
  callSessions: CallSession[];
  onUpdate: (sessionId: string, update: Partial<CallSession>) => void;
  defaultCountry: Country;
}

/**
 * Automatically refreshes contact info for call sessions.
 */
export const useCallSessionAutoRefreshContact = ({
  fields,
  callSessions,
  onUpdate,
  defaultCountry
}: CallSessionAutoRefreshContactParams) => {
  const autoRefreshSessions = callSessions.filter(session =>
    session.contactId &&
    session.autoRefreshContactInfo &&
    !session.contactInfo?.length &&
    !session.isRefreshingContactInfo);

  useEffect(() => {
    for (const session of autoRefreshSessions) {
      onUpdate(session.sessionId, { isRefreshingContactInfo: true });

      retry(() => getContact(session.contactId))
        .then(contact => {
          onUpdate(session.sessionId, {
            contactInfo: contact.fields,
            isRefreshingContactInfo: false,
            autoRefreshContactInfo: false, // Prevent infinite loop
            contactPhoneNumbers: listContactPhoneNumbers({
              fieldMapper: getFieldMapper(),
              fields,
              references: contact.fields,
              options: {
                countries: [session.country ?? defaultCountry]
              }
            }),
          });
        })
        .catch(error => {
          console.error("Error auto refreshing call session contact %s", session.contactId, error);
        });
    }

  }, [autoRefreshSessions.map(session => session.sessionId).join()]);
}

type CreateCallOrContactCommentParams = {
  message: string;
  callSession: CallSession | null;
  /**
   * Set unless providing a callSession.
   */
  contactId?: string;
  contactEventContext: ContactEventContextType;
}

/**
 * Creates a comment on a call. If a comment cannot be created on the call,
 * a comment is created on the contact instead.
 */
export async function createCallOrContactComment({
  callSession, contactEventContext, message, contactId
}: CreateCallOrContactCommentParams) {
  contactId = contactId ?? callSession?.contactId;

  try {
    const callId = await callSession.resolveCallId();
    const result = await createCallComment(callId, message);
    contactEventContext.addComments({
      calls: [
        {
          callId,
          comment: new Comment({
            date: new Date(),
            id: result.commentId,
            message,
            userId: getUserId()
          })
        }
      ],
      contacts: [],
      offers: [],
      orders: []
    });

  } catch (error) {
    if (!contactId) {
      throw error;
    }

    console.error("Error creating call comment, creating comment on contact %s instead", contactId, error);

    const result = await createContactComment(contactId, message);
    contactEventContext.addComments({
      contacts: [
        {
          contactId,
          comment: new Comment({
            date: new Date(),
            id: result.commentId,
            message,
            userId: getUserId()
          })
        }
      ],
      calls: [],
      offers: [],
      orders: []
    });
  }
}

export const nullPhoneNumber = "+46700000000";

//  Om ett telefonnummer är required i API men vi vill skicka ett null nummer
//  så detekterar denna funktion om det är det.
export function isNullPhoneNumber(number: string) {
  return number === nullPhoneNumber;
}